import { useState, useEffect } from "react";
import { API, axios } from "@/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Link2, Unlink, Clock, HelpCircle, Check, Lock, Users } from "lucide-react";

const POS_SYSTEMS = [
  { id: "square", name: "Square", connectable: true },
  { id: "spoton", name: "SpotOn", connectable: true },
  { id: "toast", name: "Toast", connectable: false, comingSoon: true, desc: "Full service restaurants" },
  { id: "clover", name: "Clover", connectable: false, comingSoon: true, desc: "Small businesses and food trucks" },
  { id: "lavu", name: "Lavu", connectable: false, comingSoon: true, desc: "Food trucks and mobile vendors" },
  { id: "touchbistro", name: "TouchBistro", connectable: false, comingSoon: true, desc: "iPad based restaurant management" },
  { id: "revel", name: "Revel", connectable: false, comingSoon: true, desc: "Enterprise chains" },
];

export default function VendorPOSSettings() {
  const { user } = useAuth();
  const [connections, setConnections] = useState({});
  const [communityRequested, setCommunityRequested] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(null);
  const [showHelp, setShowHelp] = useState(false);

  const tier = user?.analytics_tier || "free";
  const hasPOSAccess = tier !== "free";

  useEffect(() => {
    loadConnections();
  }, []);

  const loadConnections = async () => {
    try {
      const [connRes, crRes] = await Promise.all([
        axios.get(`${API}/pos/connections`),
        axios.get(`${API}/pos/community-requested`).catch(() => ({ data: { community_requested: [] } })),
      ]);
      setConnections(connRes.data);
      setCommunityRequested(crRes.data.community_requested || []);
    } catch {
      toast.error("Failed to load POS connections");
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async (posId) => {
    if (!hasPOSAccess) {
      toast.error("POS connections require POC tier or above");
      return;
    }
    setConnecting(posId);
    try {
      const res = await axios.get(`${API}/pos/${posId}/connect`);
      if (res.data.available && res.data.authorize_url) {
        window.open(res.data.authorize_url, "_blank");
      } else {
        toast.info(res.data.message || "This POS integration is not yet configured.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start connection");
    } finally {
      setConnecting(null);
    }
  };

  const handleDisconnect = async (posId) => {
    try {
      await axios.post(`${API}/pos/${posId}/disconnect`);
      toast.success(`${posId.charAt(0).toUpperCase() + posId.slice(1)} disconnected`);
      loadConnections();
    } catch {
      toast.error("Failed to disconnect");
    }
  };

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map(i => (
          <div key={i} className="h-20 bg-white/5 rounded-xl animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="pos-settings">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-sm font-bold text-white">Connected Tools</h3>
        <button
          onClick={() => setShowHelp(!showHelp)}
          className="text-white/30 hover:text-white/50 transition"
          data-testid="pos-help-btn"
        >
          <HelpCircle className="w-4 h-4" />
        </button>
      </div>

      {showHelp && (
        <div className="bg-[#F59E0B]/10 border border-[#F59E0B]/20 rounded-xl p-3 mb-3" data-testid="pos-help-guide">
          <p className="text-[11px] text-white/60 font-medium mb-2">How to connect your POS:</p>
          <div className="flex gap-3">
            <div className="text-center flex-1">
              <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center mx-auto mb-1">
                <span className="text-xs font-bold text-[#F59E0B]">1</span>
              </div>
              <p className="text-[9px] text-white/40">Tap Connect</p>
            </div>
            <div className="text-center flex-1">
              <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center mx-auto mb-1">
                <span className="text-xs font-bold text-[#F59E0B]">2</span>
              </div>
              <p className="text-[9px] text-white/40">Log in to POS</p>
            </div>
            <div className="text-center flex-1">
              <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center mx-auto mb-1">
                <span className="text-xs font-bold text-[#F59E0B]">3</span>
              </div>
              <p className="text-[9px] text-white/40">Tap Allow</p>
            </div>
          </div>
        </div>
      )}

      {/* Connectable POS systems */}
      {POS_SYSTEMS.filter(p => p.connectable).map(pos => {
        const conn = connections[pos.id] || {};
        const isConnected = conn.connected;
        const isAvailable = conn.available;

        return (
          <div
            key={pos.id}
            className={`bg-[#2C2C2E] rounded-xl p-4 border ${isConnected ? "border-emerald-500/30" : "border-white/5"}`}
            data-testid={`pos-card-${pos.id}`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isConnected ? "bg-emerald-500/10" : "bg-white/5"}`}>
                  <Link2 className={`w-5 h-5 ${isConnected ? "text-emerald-400" : "text-white/30"}`} />
                </div>
                <div>
                  <p className="text-sm font-bold text-white">{pos.name}</p>
                  {isConnected ? (
                    <div className="flex items-center gap-1.5">
                      <Check className="w-3 h-3 text-emerald-400" />
                      <span className="text-[10px] text-emerald-400 font-medium" data-testid={`pos-connected-badge-${pos.id}`}>Connected</span>
                      {conn.last_sync_at && (
                        <span className="text-[9px] text-white/20 ml-1">
                          Synced {new Date(conn.last_sync_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  ) : (
                    <p className="text-[10px] text-white/30">
                      {!hasPOSAccess ? "Requires POC tier" : isAvailable ? "Ready to connect" : "Coming soon"}
                    </p>
                  )}
                </div>
              </div>
              <div>
                {isConnected ? (
                  <button
                    onClick={() => handleDisconnect(pos.id)}
                    className="px-3 py-1.5 rounded-lg bg-red-500/10 text-red-400 text-[10px] font-bold hover:bg-red-500/20 transition"
                    data-testid={`pos-disconnect-${pos.id}`}
                  >
                    <Unlink className="w-3 h-3 inline mr-1" />Disconnect
                  </button>
                ) : !hasPOSAccess ? (
                  <div className="flex items-center gap-1 text-white/20">
                    <Lock className="w-3 h-3" />
                    <span className="text-[10px]">Upgrade to POC</span>
                  </div>
                ) : !isAvailable ? (
                  <span className="text-[9px] text-white/20 px-2 py-1 rounded bg-white/5">Setup pending</span>
                ) : (
                  <button
                    onClick={() => handleConnect(pos.id)}
                    disabled={connecting === pos.id}
                    className="px-3 py-1.5 rounded-lg bg-[#F59E0B]/10 text-[#F59E0B] text-[10px] font-bold hover:bg-[#F59E0B]/20 transition disabled:opacity-50"
                    data-testid={`pos-connect-${pos.id}`}
                  >
                    {connecting === pos.id ? "..." : `Connect ${pos.name}`}
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })}

      {/* Coming Soon POS systems */}
      {POS_SYSTEMS.filter(p => p.comingSoon).map(pos => (
        <div
          key={pos.id}
          className="bg-[#2C2C2E]/50 rounded-xl p-4 border border-white/5"
          data-testid={`pos-card-${pos.id}`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center">
                <Link2 className="w-5 h-5 text-white/15" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-sm font-bold text-white/50">{pos.name}</p>
                  <span className="text-[8px] font-bold text-amber-500 bg-amber-500/10 px-1.5 py-0.5 rounded-full" data-testid={`pos-coming-soon-${pos.id}`}>Coming Soon</span>
                </div>
                <p className="text-[10px] text-white/25 mt-0.5">{pos.desc}</p>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Community Requested POS systems */}
      {communityRequested.length > 0 && communityRequested.map(cr => (
        <div
          key={cr.name}
          className="bg-[#2C2C2E]/50 rounded-xl p-4 border border-purple-500/10"
          data-testid={`pos-card-community-${cr.name}`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                <Users className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-sm font-bold text-white/60">{cr.name}</p>
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] text-purple-400 font-medium">Community Requested</span>
                  <span className="text-[9px] text-white/20">({cr.requests} requests)</span>
                </div>
              </div>
            </div>
            <span className="text-[9px] text-white/20 px-2 py-1 rounded bg-white/5 max-w-[120px] text-right">
              We heard you — this integration is on our roadmap
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}
